/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import React, { useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiClient from '../api/client';

/**
 * CaptureRedirect — handles /c/:shortToken URLs.
 *
 * Calls the backend to resolve the short token into the full capture URL,
 * then redirects the browser to the resolved URL (which may be on the same
 * or a different subdomain).
 *
 * Errors (expired, deleted, not_found, completed) are forwarded to /error.
 */
const CaptureRedirect = () => {
  const { shortToken } = useParams();
  const navigate = useNavigate();
  const hasFetched = useRef(false);

  useEffect(() => {
    // Guard against React StrictMode double-invoke in development
    if (hasFetched.current) { return; }
    hasFetched.current = true;

    if (!shortToken) {
      navigate('/error?type=not_found', { replace: true });
      return;
    }

    const resolve = async () => {
      try {
        // Extract slug from current hostname — e.g. "acme" from "acme.lvh.me"
        const hostname = window.location.hostname;
        const slug = hostname.includes('.') ? hostname.split('.')[0] : '';

        const response = await apiClient.get(`/c/${encodeURIComponent(shortToken)}`, {
          headers: slug ? { 'x-slug': slug } : {},
        });
        const data = response.data;

        if (data?.redirectUrl) {
          // Full redirect — handles same-subdomain and cross-subdomain cases
          window.location.replace(data.redirectUrl);
        } else {
          const errorType = data?.type || 'default';
          navigate(`/error?type=${errorType}`, { replace: true });
        }
      } catch (err) {
        const errorType = err?.response?.data?.type || 'default';
        navigate(`/error?type=${errorType}`, { replace: true });
      }
    };

    resolve();
  }, [shortToken, navigate]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-base-100">
      <span className="loading loading-spinner loading-lg text-primary" />
    </div>
  );
};

export default CaptureRedirect;
